import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import SparkleIcon from "Components/common/SparkleIcon";
import copilotStore from "Stores/copilotStore";
import { dataSearchState } from "Pages/Dashboard/dataSearchV2/state";
import { applyListBuilderFilters, describeFilters } from "../../agent/pageFilters";

// Applied once per block per page load. Reopening an old chat must not
// re-run a search the user did not ask for, so a block only applies while
// its turn is live (the store is streaming) and the user is on the page.
const applied = new Set();
const onListBuilder = () => window.location.pathname.startsWith("/list-builder");

/**
 * The copilot handed a people search to the List Builder page instead of
 * drawing a table in the chat. This block sets the page's filters, runs its
 * search, and shows what was applied plus the live count from the page.
 */
const PageFiltersBlock = observer(({ block }) => {
  const [filters, setFilters] = useState(null);
  const [state, setState] = useState("idle"); // idle | applying | done | skipped

  useEffect(() => {
    if (applied.has(block.id)) return;
    applied.add(block.id);
    if (block.target !== "list_builder" || !onListBuilder() || !copilotStore.streaming) {
      setState("skipped");
      return;
    }
    setState("applying");
    applyListBuilderFilters(block.filters)
      .then(f => {
        setFilters(f);
        setState("done");
      })
      .catch(() => setState("skipped"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id]);

  const chips = describeFilters(filters || {});
  const total = Number(dataSearchState.contactTableData?.totalContacts) || 0;
  const loading = dataSearchState.discoverTableLoading;

  if (state === "skipped") {
    return (
      <div className="copilotPageFilters copilotPageFilters--skipped">
        <SparkleIcon />
        <span>Filters were applied on the List Builder page.</span>
      </div>
    );
  }

  return (
    <div className="copilotPageFilters">
      <div className="copilotPageFilters__head">
        <SparkleIcon />
        <span className="copilotPageFilters__title">
          {state === "applying" || loading
            ? "Applying on the page…"
            : total > 0
              ? `${total.toLocaleString()} matches on the page`
              : "Applied on the page"}
        </span>
      </div>
      {chips.length > 0 && (
        <div className="copilotPageFilters__chips">
          {chips.map(c => (
            <span className="copilotPageFilters__chip" key={c.label}>
              <span className="copilotPageFilters__chipLabel">{c.label}</span>
              {c.value}
            </span>
          ))}
        </div>
      )}
    </div>
  );
});

export default PageFiltersBlock;
